import React, { useState } from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Typography, Button, useMediaQuery, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet';
import ScriptDoc from './components/ScriptDoc/ScriptDoc.Component';
import FlowsDoc from './components/FlowsDoc/FlowsDoc.Component';
import RequestVariables from './components/Variables/RequestVariables.Component';
import CollectionVariables from './components/Variables/CollectionVariables.Component';
import EnvVariables from './components/Variables/EnvVariables.Component';
import GlobalVariables from './components/Variables/GlobalVariables.Component';
import VariablesOverview from './components/Variables/Overview.Component';
import Validations from './components/ValidationsDoc/Validations.Component';
import OverviewTab from './components/SendRequest/Overview.Component';
import HeadersTab from './components/SendRequest/Headers.Component';
import BodyTab from './components/SendRequest/Body.Component';
import QueryParamsTab from './components/SendRequest/Params.Component';
import ScriptOverview from './components/ScriptDoc/ScriptOverview.Component';
import InbuiltNodeModules from './components/ScriptDoc/InbuiltNodeModules.Component';
import ExternalNodeModules from './components/ScriptDoc/ExternalNodeModules.Component';
import JSFile from './components/ScriptDoc/JSFile.Component';
import RequestDocumentation from './components/APIsDocumentation/RequestDocumentation.Component';
import CollectionDocumentation from './components/APIsDocumentation/CollectionDocumentation.Component';
import APIDocumentationOverview from './components/APIsDocumentation/Overview.Component';
import OpenAPIOverview from './components/SwaggerDoc/SwaggerDoc.Component';
import ForLoop from './components/FlowsDoc/ForLoop.Component';
import IfCondition from './components/FlowsDoc/IfCondition.Component';

// Tree structure data
const treeData = [
    {
        itemId: '1',
        label: 'Introduction',
        children: [{ itemId: '2', label: 'About HawkClient' }],
    },
    {
        itemId: '3',
        label: 'Getting Started',
        children: [
            { itemId: '4', label: 'Download' },
            { itemId: '5', label: 'Create Workspace' },
            { itemId: '6', label: 'Create Collection' },
            { itemId: '7', label: 'Create Request' },
        ],
    },
    {
        itemId: '10',
        label: 'Variables',
        children: [
            { itemId: 'VariablesOverView', label: 'Variables Overview' },
            { itemId: 'RequestVariables', label: 'Request Variables' },
            { itemId: 'CollectionVariables', label: 'Collection Variables' },
            { itemId: 'EnvVariables', label: 'Env Variables' },
            { itemId: 'GlobalVariables', label: 'Global Variables' },
        ],
    },
    {
        itemId: 'SendRequest',
        label: 'Send Request',
        children: [
            { itemId: 'RequestOverview', label: 'Overview' },
            { itemId: 'Headers', label: 'Headers' },
            { itemId: 'Body', label: 'Body' },
            { itemId: 'Params', label: 'Params' }
        ],
    },
    { itemId: '9', label: 'Validations' },
    {
        itemId: 'Scripting',
        label: 'Scripting',
        children: [
            { itemId: 'ScriptOverview', label: 'Overview' },
            { itemId: 'ScriptDoc', label: 'Script Functions' },
            { itemId: 'JSFile', label: 'JS File' },
            { itemId: 'ExternalNodeModules', label: 'External Node Modules' },
            { itemId: 'InbuiltNodeModules', label: 'Inbuilt Node Modules' },
        ],
    },
    {
        itemId: 'API Flows',
        label: 'API Flows',
        children: [
            { itemId: 'FlowsOverview', label: 'Overview' },
            { itemId: 'ForLoop', label: 'For Loop' },
            { itemId: 'IfCondition', label: 'If Condition' }
        ]
    },
    {
        itemId: 'DocumentAPIs',
        label: 'Document APIs',
        children: [
            { itemId: 'APIsDocumentationOverview', label: 'Overview' },
            { itemId: 'CollectionDocumentation', label: 'Collection Documentation' },
            { itemId: 'RequestDocumentation', label: 'API Request Documentation' },
        ],
    },
    {
        itemId: 'OpenAPI',
        label: 'OpenAPI Specs',
        children: [
            { itemId: 'OpenAPISpecs', label: 'Overview' }
        ],
    },
];

// Components for different topics
const AboutHawkClient = () => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography variant='h1' sx={{ fontWeight: '600', fontSize: '24px' }}>What is HawkClient?</Typography>
                <Typography>
                    HawkClient - A developer and git friendly offline only API client designed for efficient API testing and development. Test, debug, and manage APIs efficiently with a free Postman/Insomnia alternative.
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography variant='h2' sx={{ fontWeight: '600', fontSize: '24px' }}>Team Collaboration</Typography>
                <Typography>
                    HawkClient stores the API requests and collections in .yaml files. Theses files can be shared with team using any version control system like git.
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography variant='h2' sx={{ fontWeight: '600', fontSize: '24px' }}>Privacy</Typography>
                <Typography>
                    HawkClient does not require any sign up or sign in nor it collects any user data. HawkClient focuses on offline only approach making it secure and relaible API testing tool.
                </Typography>
            </Box>
        </Box>
    )
};
const Download = () => {
    const navigate = useNavigate();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: "10px" }}>
            <Typography variant="h1" sx={{ fontWeight: 600, fontSize: '24px' }}>How to download HawkClient</Typography>
            <Typography>Please proceed to the download section by clicking on the below download button</Typography>
            <Button sx={{ width: '100px' }} onClick={() => navigate('/download')}>
                Download
            </Button>
            <Typography>Download page will provide you the option to download the app for your OS.</Typography>
        </Box>
    )
};
const CreateWorkspace = () => {
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <Box style={{
            display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflowY: 'auto',
            p: 3
        }}>
            <Typography variant='h1' sx={{ fontSize: '24px', fontWeight: 600 }}>How to create a workspace</Typography>
            <Typography variant="body1" style={{ width: '100%', maxWidth: '800px' }}>
                <p>
                    Click on <strong>+ workspace</strong> icon on right side of the header.
                </p>
            </Typography>
            <img
                src="/createworkspace.png"
                alt="validations"
                style={{
                    width: isMediumScreen ? '100%' : '65%',
                    // maxWidth: '800px',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    marginTop: '8px',
                }}
            />
            <Typography variant="body1" style={{ width: '100%', maxWidth: '800px' }}>
                <p>
                    A Dialog box will appear in mid screen, asking you the <strong>workspace name</strong> and the <strong>directory</strong> where you want to create your workspace.
                    <br /><br />
                    <strong>Directory field</strong> is optional and by default it will store the workspace in documents under hawkClient folder.
                    <br /><br />
                    It is recommended to point the workspace location to your github repository, using which you can push your workspace files to github making it available to other teammates.
                </p>
            </Typography>
        </Box>

    )
};
const CreateCollection = () => {
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <Box style={{
            display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflowY: 'auto',
            p: 3
        }}>
            <Typography variant='h1' sx={{ fontSize: '24px', fontWeight: 600 }}>How to create a collection</Typography>
            <Typography variant="body1" style={{ width: '100%', maxWidth: '800px' }}>
                <p>
                    Click on Add collection <strong>+</strong> icon under <strong>collections</strong> tab on top of left side bar.
                </p>
            </Typography>
            <img
                src="/addcollection.png"
                alt="validations"
                style={{
                    width: isMediumScreen ? '100%' : '65%',
                    // maxWidth: '800px',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    marginTop: '8px',
                }}
            />
            <Typography variant="body1" style={{ width: '100%', maxWidth: '800px' }}>
                <p>
                    A Dialog box will appear in mid screen, asking you the <strong>collection name</strong>, <strong>description</strong> and the <strong>directory</strong> where you want to create your collection.
                    <br /><br />
                    <strong>Directory field</strong> is optional and by default it will store the collection under the workspace folder.
                    <br /><br />
                    It is recommended to keep the collection directory field empty so that all of your collections can reside under one github repo i.e your workspace location/repo.
                </p>
            </Typography>
        </Box>
    )
};


const CreateRequest = () => {
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <Box style={{
            display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflowY: 'auto',
            p: 3
        }}>
            <Typography variant='h1' sx={{ fontSize: '24px', fontWeight: 600 }}>How to create a request</Typography>
            <Typography variant="body1" style={{ width: '100%', maxWidth: '800px' }}>
                <p>
                    Click on vertical <strong>...</strong> button under your selected collection and folder, followed by <strong>Add Request</strong> menu button under <strong>dropdown</strong>.
                </p>
            </Typography>
            <img
                src="/addrequest1.png"
                alt="validations"
                style={{
                    width: isMediumScreen ? '100%' : '65%',
                    // maxWidth: '800px',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    marginTop: '8px',
                }}
            />

            <Typography variant="body1" style={{ width: '100%', maxWidth: '800px', marginTop: '8px' }}>
                <p>
                    A Dialog box will appear in mid screen, asking you the <strong>request name</strong>.
                    <br /><br />
                    Provide the unique request name and click on <strong>create</strong> button at the bottom of dialog box.
                </p>
            </Typography>
            <img
                src="/addrequest2.png"
                alt="validations"
                style={{
                    width: isMediumScreen ? '100%' : '65%',
                    // maxWidth: '800px',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    marginTop: '8px',
                }}
            />
            <Typography variant="body1" style={{ width: '100%', maxWidth: '800px', marginTop: '8px' }}>
                <p>
                    After that your request will be created successfully. Now you can proceed to fill in the other details like Url, Headers, Auth and send your first request.
                </p>
            </Typography>
        </Box>
    )
};

// Component that maps content based on the selected itemId
const getContentComponent = (itemId) => {
    switch (itemId) {
        case '2':
            return <AboutHawkClient />;
        case '4':
            return <Download />;
        case '5':
            return <CreateWorkspace />;
        case '6':
            return <CreateCollection />;
        case '7':
            return <CreateRequest />;
        case 'ScriptOverview':
            return <ScriptOverview />;
        case 'ScriptDoc':
            return <ScriptDoc />;
        case 'InbuiltNodeModules':
            return <InbuiltNodeModules />;
        case 'ExternalNodeModules':
            return <ExternalNodeModules />;
        case 'JSFile':
            return <JSFile />;
        case '9':
            return <Validations />;
        case 'RequestVariables':
            return <RequestVariables />;
        case 'FlowsOverview':
            return <FlowsDoc />;
        case 'ForLoop':
            return <ForLoop />;
        case 'IfCondition':
            return <IfCondition />;
        case 'CollectionVariables':
            return <CollectionVariables />;
        case 'EnvVariables':
            return <EnvVariables />;
        case 'GlobalVariables':
            return <GlobalVariables />;
        case 'VariablesOverView':
            return <VariablesOverview />;
        case 'RequestOverview':
            return <OverviewTab />;
        case 'Headers':
            return <HeadersTab />;
        case 'Body':
            return <BodyTab />;
        case 'Params':
            return <QueryParamsTab />;
        case 'RequestDocumentation':
            return <RequestDocumentation />
        case 'CollectionDocumentation':
            return <CollectionDocumentation />
        case 'APIsDocumentationOverview':
            return <APIDocumentationOverview />
        case 'OpenAPISpecs':
            return <OpenAPIOverview />
        default:
            return <Typography>Select a topic to view details.</Typography>;
    }
};

const Docs = () => {
    const [selectedItemId, setSelectedItemId] = useState("2");
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Recursive function to render tree items
    const renderTreeItems = (nodes) =>
        nodes.map((node) => (
            <TreeItem
                key={node.itemId}
                itemId={node.itemId}
                label={node.label}
                nodeId={node.itemId}
            >
                {node.children ? renderTreeItems(node.children) : null}
            </TreeItem>
        ));

    // Handle item selection
    const handleNodeSelect = (event, itemId) => {
        const node = findNodeById(treeData, itemId);
        if (!node.children) {
            // Only update content if it's a leaf node
            setSelectedItemId(itemId);
        }
    };

    // Find a node by its itemId
    const findNodeById = (nodes, id) => {
        for (let node of nodes) {
            if (node.itemId === id) return node;
            if (node.children) {
                const found = findNodeById(node.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    return (
        <>
            <Helmet>
                <title>Docs - HawkClient</title>
                <meta
                    name="description"
                    content="View the documentation for HawkClient, a developer-friendly offline-only API client."
                />
            </Helmet>
            <Box sx={{ display: 'flex', height: '100vh', flexDirection: isMediumScreen ? 'column' : 'row' }}>
                {/* Left Panel - Tree View */}
                {isMediumScreen ? (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            Tabs
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{}}>
                                <SimpleTreeView
                                    slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
                                    onSelectedItemsChange={handleNodeSelect}
                                    defaultExpandedItems={['1']}
                                    defaultSelectedItems={['2']}
                                >
                                    {renderTreeItems(treeData)}
                                </SimpleTreeView>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ) : (
                    <Box
                        sx={{
                            width: '300px',
                            borderRight: '1px solid #ddd',
                            borderBottom: '0px',
                            p: 2,
                        }}
                    >
                        <SimpleTreeView
                            slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
                            onSelectedItemsChange={handleNodeSelect}
                            defaultExpandedItems={['1']}
                            defaultSelectedItems={['2']}
                        >
                            {renderTreeItems(treeData)}
                        </SimpleTreeView>
                    </Box>
                )}

                {/* Right Panel - Content View */}
                <Box sx={{ flex: 1, p: 3, overflow: 'scroll' }}>
                    {getContentComponent(selectedItemId)}
                </Box>
            </Box>
        </>
    );
};

export default Docs;
